import { useCallback, useEffect, useState } from 'react'
import { find } from 'lodash'

import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox } from '@/components/ui'
import { Icon } from '@/common/components/Icon'
import Loader from '../../../common/components/Loader'
import { CheckInField, Languages } from '../../../common/models'
import { IColumn, Table } from '../../../core/components/Table'
import { useTableParams } from '../../../core/hooks/table-params'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import {
  deleteCheckInField,
  getAllCheckInFields,
  updateCheckInFieldsOrder,
} from '../../../core/services/check-in-fields'
import { getSettingsLanguages } from '../../../core/services/festival-settings'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { Pagination } from '../Pagination'
import { CreateCheckInFieldModal } from './CreateCheckInFieldModal'

export const CheckInFieldsPage: React.FC = () => {
  const { page, setPage, size, setSize, sortBy, setSortBy, totalElements } =
    useTableParams()

  const { hasEditPermission } = useCheckPermission({ page: 'SETTINGS' })

  const [showModal, setShowModal] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [allLanguages, setAllLanguages] = useState<Languages[]>([])
  const [currentField, setCurrentField] = useState<CheckInField>()
  const [fields, setFields] = useState<CheckInField[]>([])
  const [isCheckAll, setIsCheckAll] = useState(false)
  const [isCheck, setIsCheck] = useState<string[]>([])
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState(false)
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >(undefined)

  const [isError, setIsError] = useState(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState(false)
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        const response = await getSettingsLanguages()
        if (response.status === 200) {
          setAllLanguages(response.data)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  const getData = useCallback(async () => {
    try {
      setLoading(true)
      const { data } = await getAllCheckInFields()
      setFields(data)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    getData()
  }, [getData])

  const handleDelete = () => {
    if (!hasEditPermission || isCheck.length === 0) return
    setDeleteConfirmationModal(true)
  }

  const deleteFunc = () => {
    // prettier-ignore
    (async () => {
      try {
        const { status } = await deleteCheckInField(isCheck)
        if (status === 204 || status === 200) {
          const { data } = await getAllCheckInFields()
          setFields(data)
          setIsCheck([])
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Delete Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }

  const handleSelectAll = () => {
    if (!hasEditPermission) return
    setIsCheckAll(!isCheckAll)
    setIsCheck(
      isCheckAll
        ? []
        : fields.map((f) => f.id!).filter((id) => id !== undefined),
    )
  }

  const handleClick = (id: string, checked: boolean) => {
    if (!hasEditPermission) return
    setIsCheck(checked ? [...isCheck, id] : isCheck.filter((i) => i !== id))
  }

  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      if (sortDirection === 'ASC') {
        setSortDirection('DESC')
      } else {
        setSortDirection(undefined)
        setSortBy(undefined)
      }
    } else {
      setSortDirection('ASC')
      setSortBy(sort)
    }
  }

  const pageChange = async (newPage: number) => {
    setPage(newPage)
  }

  const handleReorder = async (next: CheckInField[]) => {
    if (!hasEditPermission) return
    const previous = fields
    setFields(next) // optimistic

    try {
      // Persist every row's absolute ordinal (1-based), sequentially in order
      // to avoid the backend races seen with concurrent writes.
      for (let index = 0; index < next.length; index += 1) {
        const f = next[index]
        if (f.id) await updateCheckInFieldsOrder(f.id, index + 1)
      }
    } catch (e) {
      setFields(previous) // revert on failure
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const columns: IColumn[] = [
    {
      title: (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={handleSelectAll}
          checked={isCheckAll}
        />
      ),
      accessor: '',
      render: (row) => (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
          checked={isCheck.includes(row.id)}
        />
      ),
    },
    {
      title: 'Name',
      accessor: 'name',
      sortable: true,
      render: (row) => (
        <div className="text-subtitle-m min-w-[220px] text-monochrome-1">
          {row.name}
        </div>
      ),
    },
    {
      title: 'Mandatory',
      accessor: 'mandatory',
      render: (row) => (
        <span className="text-body-medium text-monochrome-1">
          {row.mandatory ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      title: 'Published',
      accessor: 'published',
      render: (row) => (
        <Checkbox
          checked={row.published}
          disabled
          className="pointer-events-none"
        />
      ),
    },
    {
      title: 'Edit',
      accessor: '',
      render: (row) => (
        <a
          className="view text-accent-1 underline"
          href="#"
          onClick={(e) => {
            e.preventDefault()
            if (!hasEditPermission) return
            setEditMode(true)
            setCurrentField(fields.find((f) => f.id === row.id))
            setShowModal(true)
          }}
        >
          Edit
        </a>
      ),
    },
  ]

  return (
    <>
      <Header
        pageTitle="Check-In Fields"
        actions={
          <>
            {hasEditPermission && isCheck.length >= 1 && (
              <Button variant="destructive" onClick={handleDelete}>
                Delete
                <Icon
                  icon="close-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            )}
            {hasEditPermission && (
              <Button
                variant="primary-bordered"
                onClick={() => {
                  setEditMode(false)
                  setShowModal(true)
                  setCurrentField(undefined)
                }}
              >
                Add New
                <Icon
                  icon="plus-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                />
              </Button>
            )}
          </>
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle title="Check-In Fields" className="mb-5" icon="text" />
        <Table
          className="[&_th]:!whitespace-nowrap"
          handleSortBy={handleSortBy}
          isCheck={isCheck}
          columns={columns}
          data={fields}
          rowReorder
          alignment="middle"
          onRowReorder={handleReorder}
        />
      </Card>
      <Pagination
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={pageChange}
      />
      <CreateCheckInFieldModal
        allLanguages={allLanguages}
        field={currentField}
        showModal={showModal}
        setShowModal={setShowModal}
        setFields={setFields}
        editMode={editMode}
      />
      <DeleteConfirmationModal
        size="md"
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={isCheck.length > 1 ? 'Check-In Fields' : 'Check-In Field'}
        deleteFunc={deleteFunc}
        deletedItem={
          isCheck.length > 1
            ? `${isCheck.length} check-in fields`
            : find(fields, { id: isCheck[0] })?.name
        }
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
